import React from "react";
import PropTypes from "prop-types";
import { Link as RouterLink } from "react-router-dom";

import PageShell from "../common/PageShell";

import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Avatar from "@mui/material/Avatar";
import EloBadge from "../common/EloBadge.js";

import _ from "lodash";

export default function FindPlayerPage({ catalog, actions }) {
  const user = catalog?.user;
  const results = catalog?.friendSearchResults || [];

  const [q, setQ] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  const debouncedSearch = React.useMemo(
    () =>
      _.debounce((value) => {
        const trimmed = (value || "").trim();
        if (!trimmed) {
          actions.clearUserSearchResults && actions.clearUserSearchResults();
          setLoading(false);
          return;
        }
        setLoading(true);
        actions.searchUsers({ q: trimmed, limit: 25 });
        // best-effort: clear spinner shortly after results should land
        setTimeout(() => setLoading(false), 250);
      }, 200),
    [actions]
  );

  React.useEffect(() => {
    return () => {
      debouncedSearch.cancel();
      actions.clearUserSearchResults && actions.clearUserSearchResults();
    };
  }, []);

  const handleChange = (e) => {
    const value = e.target.value;
    setQ(value);
    debouncedSearch(value);
  };

  const hasResults = Array.isArray(results) && results.length > 0;

  return (
    <PageShell maxWidth="lg">
      <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
        <Typography sx={{ fontSize: 20, fontWeight: 800 }}>
          Find Player
        </Typography>
        <Typography sx={{ color: "#999999", mt: 0.25, fontSize: 13 }}>
          Search by nickname, or by Steam ID prefix.
        </Typography>

        <Box sx={{ mt: 2, display: "flex", gap: 1, alignItems: "center" }}>
          <TextField
            fullWidth
            size="small"
            label="Search"
            value={q}
            onChange={handleChange}
            placeholder="e.g. s1mple, zywoo, 7656..."
          />
          {loading && <CircularProgress size={18} />}
        </Box>

        <Box sx={{ mt: 2 }}>
          {!user && <Typography>Please wait...</Typography>}

          {user && !q.trim() && (
            <Typography sx={{ color: "#999999" }}>
              Start typing to search.
            </Typography>
          )}

          {user && q.trim() && !loading && !hasResults && (
            <Typography sx={{ color: "#999999" }}>
              No players found.
            </Typography>
          )}

          {user && hasResults && (
            <TableContainer sx={{ borderRadius: 2, border: "1px solid rgba(255,255,255,0.08)" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 800 }}>Player</TableCell>
                    <TableCell sx={{ width: 120, fontWeight: 800 }}>ELO</TableCell>
                    <TableCell sx={{ width: 200, fontWeight: 800 }}>Country</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {results.map((row) => {
                    const profileLink = row?._id ? `/profile/${row._id}` : null;
                    return (
                      <TableRow key={`${row?._id || row?.steamId}`} hover>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                            <Avatar
                              src={row?.picture || undefined}
                              alt={row?.nickname || "Player"}
                              sx={{ width: 28, height: 28 }}
                            />
                            <Box sx={{ display: "flex", flexDirection: "column" }}>
                              {profileLink ? (
                                <Typography
                                  component={RouterLink}
                                  to={profileLink}
                                  sx={{
                                    color: "inherit",
                                    textDecoration: "none",
                                    fontWeight: 700,
                                    "&:hover": { textDecoration: "underline" },
                                  }}
                                >
                                  {row?.nickname || "Unknown"}
                                </Typography>
                              ) : (
                                <Typography sx={{ fontWeight: 700 }}>
                                  {row?.nickname || "Unknown"}
                                </Typography>
                              )}
                              <Typography sx={{ color: "#999999", fontSize: 12 }}>
                                {row?.steamId || ""}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <EloBadge value={row?.elo} size="small" />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            {row?.flagImageUrl ? (
                              <Box
                                component="img"
                                src={row.flagImageUrl}
                                alt={row?.country || "Country"}
                                sx={{
                                  width: 22,
                                  height: 16,
                                  borderRadius: "2px",
                                  objectFit: "cover",
                                }}
                              />
                            ) : (
                              <Box sx={{ width: 22, height: 16 }} />
                            )}
                            <Typography sx={{ fontSize: 13, color: "#cccccc" }}>
                              {row?.country || "-"}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Paper>
    </PageShell>
  );
}

FindPlayerPage.propTypes = {
  catalog: PropTypes.object.isRequired,
  actions: PropTypes.object.isRequired,
};

